import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { NOTIFICATION_TYPE_CONFIG } from '../../pages/alerts-management/components/AlertTypeConfig';
import useNotifications from '../../hooks/useNotifications';
import useAlerts from '../../hooks/useAlerts';

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr)?.getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const { alerts } = useAlerts();
  const activeAlertCount = alerts?.filter(a => a?.is_active)?.length ?? 0;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const recent = notifications
    ?.filter((n) => !n?.is_dismissed)
    ?.slice(0, 10);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref?.current && !ref?.current?.contains(e?.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemClick = (notif) => {
    markRead?.(notif?.id);
    setOpen(false);
    navigate('/alerts-management');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative flex items-center justify-center w-9 h-9 rounded-md transition-colors hover:bg-muted focus-ring"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        style={{ transition: 'all var(--transition-base)' }}
      >
        <Icon name="Bell" size={18} color="var(--color-foreground)" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold px-1"
            style={{
              backgroundColor: '#DC2626',
              color: '#fff',
              fontSize: '10px',
              fontFamily: 'var(--font-caption)',
              animation: 'bellPulse 2s infinite',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-2 w-80 rounded-xl border shadow-xl z-50 overflow-hidden"
          style={{
            backgroundColor: 'var(--color-card)',
            borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <div className="flex items-center gap-2">
              <Icon name="Bell" size={15} color="var(--color-foreground)" />
              <span
                className="text-sm font-semibold"
                style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
              >
                Notifications
              </span>
              {unreadCount > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                  style={{ backgroundColor: '#DC2626', color: '#fff', fontFamily: 'var(--font-caption)' }}
                >
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={(e) => { e?.stopPropagation(); markAllRead?.(); }}
                className="text-xs font-medium transition-colors hover:opacity-80"
                style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Alert Rules banner */}
          <div
            className="flex items-center justify-between px-4 py-2.5 border-b"
            style={{
              borderColor: 'var(--color-border)',
              background: 'rgba(27, 94, 32, 0.07)',
            }}
          >
            <div className="flex items-center gap-2">
              <div
                className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgba(27, 94, 32, 0.15)' }}
              >
                <Icon name="Zap" size={11} color="var(--color-primary)" />
              </div>
              <span
                className="text-xs font-medium"
                style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
              >
                {activeAlertCount > 0
                  ? <><span style={{ color: 'var(--color-primary)', fontWeight: 700 }}>{activeAlertCount}</span> active alert {activeAlertCount === 1 ? 'rule' : 'rules'}</>
                  : 'No active alert rules'}
              </span>
            </div>
            <button
              onClick={() => { setOpen(false); navigate('/alerts-management'); }}
              className="text-xs font-semibold transition-colors hover:opacity-80 flex items-center gap-1"
              style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}
            >
              Manage <Icon name="ArrowRight" size={11} color="var(--color-primary)" />
            </button>
          </div>

          {/* Notification list */}
          <div className="max-h-80 overflow-y-auto">
            {recent?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Icon name="BellOff" size={24} color="var(--color-muted-foreground)" />
                <p className="text-sm mt-2" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                  No notifications
                </p>
              </div>
            ) : (
              recent?.map((notif) => {
                const cfg = NOTIFICATION_TYPE_CONFIG?.[notif?.type] || NOTIFICATION_TYPE_CONFIG?.system;
                return (
                  <button
                    key={notif?.id}
                    onClick={() => handleItemClick(notif)}
                    className="w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted border-b"
                    style={{
                      borderColor: 'var(--color-border)',
                      backgroundColor: notif?.is_read ? 'transparent' : cfg?.color + '08',
                    }}
                  >
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: cfg?.color + '20' }}
                    >
                      <Icon name={cfg?.icon} size={13} color={cfg?.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-1">
                        <p
                          className="text-xs font-semibold truncate"
                          style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
                        >
                          {notif?.title}
                        </p>
                        {!notif?.is_read && (
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: cfg?.color }} />
                        )}
                      </div>
                      <p
                        className="text-xs mt-0.5 line-clamp-2"
                        style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
                      >
                        {notif?.message}
                      </p>
                      <p
                        className="text-xs mt-1"
                        style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)', opacity: 0.7 }}
                      >
                        {formatRelativeTime(notif?.created_at)}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div
            className="px-4 py-3 border-t"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <button
              onClick={() => { setOpen(false); navigate('/alerts-management'); }}
              className="w-full text-center text-xs font-medium transition-colors hover:opacity-80"
              style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}
            >
              View All Notifications →
            </button>
          </div>
        </div>
      )}
      <style>{`
        @keyframes bellPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
}
